import {
  mapValidationErrors,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { CheckPriceProps } from "Components/Features/CheckPrice";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert";
import { ErrorsSummedUpRender } from "Components/Form/ErrorsSummedUp";
import { RecentDamages } from "Components/Form/RecentDamages";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagCabinRental } from "Utils/unleash/flags";
import React from "react";
import { CABIN_ABOUT_STEP } from "..";
import { ChooseStandard } from "../../FormComponentsHouseProducts/ChooseStandard";
import { DamagedFromPests } from "../../FormComponentsHouseProducts/DamagedFromPests";
import { ElectricityQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/ElectricityQuestion";
import { RentalQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/RentalQuestion";
import { TapWaterQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/TapWaterQuestion";
import { HistoricalValue } from "../../FormComponentsHouseProducts/HistoricalValue";
import { CrawlSpaceQuestion } from "../ProductIntro/FormComponents/CrawlSpaceQuestion";
import { CabinProductIntroFormValues } from "../ProductIntro/cabinProductIntroInterfaces";
import { CABIN_INTRO_STEP } from "../cabinSteps";
import { CabinProductDetailsFormValues } from "./cabinProductDetailsInterfaces";
import { validationSchema } from "./cabinProductDetailsValidation";

export const CabinProductDetails = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const [
    {
      formText: { aboutCabin },
      details,
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CABIN));

  const {
    priceCalculation: { priceCalculationFormStates, priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const cabinProductIntroFormValues = priceCalculationFormStates[
    CABIN_INTRO_STEP.id
  ] as CabinProductIntroFormValues;

  const enableRentalToggle = useUnleashFeatureFlag(
    FeatureFlagCabinRental.ENABLE_CABIN_RENTAL,
  );

  const initialFormState = useFormState<CabinProductDetailsFormValues>(
    CABIN_ABOUT_STEP.id,
    initialFormValues,
  );

  const handleSubmit = (formProps: CabinProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: CABIN_ABOUT_STEP.id,
        formState: {
          ...formProps,
        },
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.CABIN.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        ...(formProps.injuries !== DamageRecentYears.NONE && {
          injuryYear: formProps.injuryYear,
        }),
        constructionYear: cabinProductIntroFormValues.constructionYear,
        areal: {
          bta: cabinProductIntroFormValues.grossArea,
          boa: cabinProductIntroFormValues.livingArea,
        },
        postNumber: cabinProductIntroFormValues.postNumber,
        otherBuildings: cabinProductIntroFormValues.otherBuildings,
        injuries: formProps.injuries === DamageRecentYears.NONE ? 0 : 1,
        standard: formProps.standard,
        hasPool:
          nullableFormBooleanToNullableBoolean(formProps.hasCrawlspace) ??
          false,
        hasTapWater:
          nullableFormBooleanToNullableBoolean(formProps.hasTapWater) ?? false,
        hasElectricity:
          nullableFormBooleanToNullableBoolean(formProps.hasElectricity) ??
          false,
        hasRental: enableRentalToggle
          ? nullableFormBooleanToNullableBoolean(formProps.hasRental) ?? false
          : undefined,
        pestsDamaged:
          nullableFormBooleanToNullableBoolean(formProps.pestsDamaged) ?? false,
      },
    });
  };

  return (
    <ProductDetailsTile
      header={CABIN_ABOUT_STEP.title}
      onSubmit={handleSubmit}
      initialValues={initialFormState}
      validationSchema={validationSchema()}
    >
      {(formProps: FormikProps<CabinProductDetailsFormValues>) => {
        return (
          <Form>
            <ChooseStandard
              formProps={formProps}
              aboutProductText={aboutCabin}
              lang={lang}
            />
            <HistoricalValue
              formProps={formProps}
              label={getText("cabinform.label.historicalvalue")}
            />
            <ElectricityQuestion formProps={formProps} lang={lang} />
            <TapWaterQuestion formProps={formProps} lang={lang} />
            <CrawlSpaceQuestion
              formProps={formProps}
              productText={aboutCabin}
              lang={lang}
            />
            <RentalQuestion formProps={formProps} lang={lang} />
            <DamagedFromPests
              formProps={formProps}
              label={getText("cabinform.label.pestsdamaged")}
              product={INSURANCE_NAME_VALUE.CABIN}
            />
            <RecentDamages
              formProps={formProps}
              productName={ProductBackendName.CABIN}
            />
            <BackendErrorAlert priceCalculation={priceCalculationResult} />
            <ErrorsSummedUpRender
              formProps={formProps}
              errors={mapValidationErrors(formProps.errors, details, lang)}
              isVisible
            />
            <ScrollToFirstError />
            <NavigationButtons isLoading={priceCalculationResult.isLoading} />
          </Form>
        );
      }}
    </ProductDetailsTile>
  );
};

const initialFormValues = {
  standard: undefined,
  historicalValue: undefined,
  hasElectricity: undefined,
  hasTapWater: undefined,
  hasCrawlspace: undefined,
  hasRental: undefined,
  rentalTimeExceedsOneMonthAYear: undefined,
  pestsDamaged: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
